/**
 * The state an EHR launch establishes for the session it opened: who launched
 * us (issuer, profile name, user), the credentials that launch issued
 * (launch token, access token, auth URL), the clinical context it points at
 * (patient, encounter, appointment) and the two failure flags the launch modals
 * render on.
 *
 * One atom per field rather than one for the group. The four `accessToken`
 * readers are EHR query hooks that must not re-render when a launch modal flips
 * `userNotFound`, and `profileName` moves on a different path from
 * `onboardingSessionId` entirely.
 *
 * `integrationEhrLaunchAtom` composes all eleven for the seven files that take
 * the whole object. It is derived, and therefore read-only, so it cannot become
 * a write path back to a single blob — and the seven keep destructuring exactly
 * what they destructured before.
 *
 * Not persisted: a launch is re-established from the EHR, never rehydrated.
 */
import { atom, getDefaultStore } from 'jotai';

export type EhrLaunchInfo = {
  patientId: string;
  encounterId: string;
  appointmentId?: string;
  contactSerialNumber?: string;
};

export type EhrLaunchSessionInfo = {
  linkedPatientId?: string | null;
};

export type IntegrationEhrLaunchState = {
  issuer?: string | null;
  launchToken?: string | null;
  accessToken?: string | null;
  profileName?: string | null;
  onboardingSessionId?: string | null;
  ehrLaunchInfo?: EhrLaunchInfo | null;
  ehrLaunchAuthUrl?: string | null;
  user?: string | null;
  userNotFound: boolean;
  tokenError: boolean;
  sessionInfo: EhrLaunchSessionInfo;
};

export const ehrLaunchIssuerAtom = atom<string | null | undefined>(null);
export const ehrLaunchTokenAtom = atom<string | null | undefined>(null);
export const ehrLaunchAccessTokenAtom = atom<string | null | undefined>(null);
export const ehrLaunchProfileNameAtom = atom<string | null | undefined>(null);
export const ehrLaunchOnboardingSessionIdAtom = atom<string | null | undefined>(null);
export const ehrLaunchInfoAtom = atom<EhrLaunchInfo | null | undefined>(null);
export const ehrLaunchAuthUrlAtom = atom<string | null | undefined>(null);
export const ehrLaunchUserAtom = atom<string | null | undefined>(null);
export const ehrLaunchUserNotFoundAtom = atom<boolean>(false);
export const ehrLaunchTokenErrorAtom = atom<boolean>(false);
export const ehrLaunchSessionInfoAtom = atom<EhrLaunchSessionInfo>({
  linkedPatientId: null,
});

/** The whole shape, for the seven files that take the object. */
export const integrationEhrLaunchAtom = atom<IntegrationEhrLaunchState>((get) => ({
  issuer: get(ehrLaunchIssuerAtom),
  launchToken: get(ehrLaunchTokenAtom),
  accessToken: get(ehrLaunchAccessTokenAtom),
  profileName: get(ehrLaunchProfileNameAtom),
  onboardingSessionId: get(ehrLaunchOnboardingSessionIdAtom),
  ehrLaunchInfo: get(ehrLaunchInfoAtom),
  ehrLaunchAuthUrl: get(ehrLaunchAuthUrlAtom),
  user: get(ehrLaunchUserAtom),
  userNotFound: get(ehrLaunchUserNotFoundAtom),
  tokenError: get(ehrLaunchTokenErrorAtom),
  sessionInfo: get(ehrLaunchSessionInfoAtom),
}));

export function setEhrLaunchToken(launchToken?: string | null): void {
  getDefaultStore().set(ehrLaunchTokenAtom, launchToken);
}

export function setEhrLaunchIssuer(issuer?: string | null): void {
  getDefaultStore().set(ehrLaunchIssuerAtom, issuer);
}

export function setEhrLaunchProfileName(profileName?: string | null): void {
  getDefaultStore().set(ehrLaunchProfileNameAtom, profileName);
}

export function setEhrLaunchUserNotFound(userNotFound: boolean): void {
  getDefaultStore().set(ehrLaunchUserNotFoundAtom, userNotFound);
}

export function setEhrLaunchTokenError(tokenError: boolean): void {
  getDefaultStore().set(ehrLaunchTokenErrorAtom, tokenError);
}

export function setFhirLaunchUser(user?: string | null): void {
  getDefaultStore().set(ehrLaunchUserAtom, user);
}

export function setEhrLaunchAccessToken(accessToken?: string | null): void {
  getDefaultStore().set(ehrLaunchAccessTokenAtom, accessToken);
}

export function setEhrLaunchOnboardingSessionId(
  onboardingSessionId?: string | null
): void {
  getDefaultStore().set(ehrLaunchOnboardingSessionIdAtom, onboardingSessionId);
}

export function setEhrLaunchInfo(ehrLaunchInfo?: EhrLaunchInfo | null): void {
  getDefaultStore().set(ehrLaunchInfoAtom, ehrLaunchInfo);
}

export function setEhrLaunchAuthUrl(ehrLaunchAuthUrl?: string | null): void {
  getDefaultStore().set(ehrLaunchAuthUrlAtom, ehrLaunchAuthUrl);
}

/** REPLACES the object; an absent `linkedPatientId` is absent, not preserved. */
export function setEhrLaunchSessionInfo(sessionInfo: EhrLaunchSessionInfo): void {
  getDefaultStore().set(ehrLaunchSessionInfoAtom, sessionInfo);
}

/**
 * Every field back to its initial value.
 *
 * Walks an explicit table rather than spreading a shared initial-state object,
 * for two reasons. A new atom is then a compile error here instead of a value
 * that silently survives a clear. And `sessionInfo` gets a FRESHLY BUILT object
 * each time — the reducer this replaces handed back the module's own
 * `initialState`, so two clears shared one nested object.
 */
export function clearIntegrationEhrLaunch(): void {
  const store = getDefaultStore();
  store.set(ehrLaunchIssuerAtom, null);
  store.set(ehrLaunchTokenAtom, null);
  store.set(ehrLaunchAccessTokenAtom, null);
  store.set(ehrLaunchProfileNameAtom, null);
  store.set(ehrLaunchOnboardingSessionIdAtom, null);
  store.set(ehrLaunchInfoAtom, null);
  store.set(ehrLaunchAuthUrlAtom, null);
  store.set(ehrLaunchUserAtom, null);
  store.set(ehrLaunchUserNotFoundAtom, false);
  store.set(ehrLaunchTokenErrorAtom, false);
  store.set(ehrLaunchSessionInfoAtom, { linkedPatientId: null });
}

/**
 * `onboardingSessionId` for the legacy note-generation stream and
 * `userNotFound` for the launch-token mutation's early return. Not hooks: both
 * callers are plain functions with no React context to read from.
 */
export function getEhrLaunchOnboardingSessionId(): string | null | undefined {
  return getDefaultStore().get(ehrLaunchOnboardingSessionIdAtom);
}

export function getEhrLaunchUserNotFound(): boolean {
  return getDefaultStore().get(ehrLaunchUserNotFoundAtom);
}
